import { TextField, Typography } from "@mui/material";
import { useOrganizationContext } from "context/OrganisationContext";
import * as React from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import AuthLayout from "./AuthLayout";
import * as authApi from "./helpers/authApi";
import { getErrorMessage } from "./helpers/authHelpers";
import { InputGroup, LoginButton, LoginCard, LoginContainer } from "./styles";
import { Helmet } from "react-helmet";

export interface Props {}

interface ResetPasswordFormValues {
	email: string;
}

const ResetPasswordScreen: React.FC<Props> = (props: Props) => {
	const org = useOrganizationContext();
	const [isLoading, setIsLoading] = React.useState(false);
	const [showSuccessCard, setShowSuccessCard] = React.useState(false);
	const [error, setError] = React.useState<any>(null);
	const intl = useIntl();

	const submit = async (val: ResetPasswordFormValues) => {
		setIsLoading(true);
		setError("");
		try {
			await authApi.resetPassword(val.email, org.value?._id);
			setShowSuccessCard(true);
		} catch (err) {
			const message = getErrorMessage((err as any).json.code);

			setError(message);
		} finally {
			setIsLoading(false);
		}
	};
	if (org.loading) {
		return null;
	}
	return (
		<AuthLayout>
			<Helmet>
				<title>{intl.formatMessage({ id: "RESET_PASSWORD.page.title" })}</title>
			</Helmet>
			<LoginContainer>
				<LoginCard>
					{showSuccessCard ? (
						<div style={{ marginTop: 0 }}>
							<Typography
								variant="h5"
								style={{ fontWeight: 800, fontSize: "20px", margin: "0 0 16px 0" }}
							>
								{intl.formatMessage({ id: "RESET_PASSWORD.success-reset-form.title" })}
							</Typography>
							<Typography variant="caption" style={{ fontWeight: "normal", fontSize: "18px" }}>
								{intl.formatMessage({ id: "RESET_PASSWORD.success-reset-form.description" })}
							</Typography>
						</div>
					) : (
						<ResetPasswordForm isLoading={isLoading} submit={submit} />
					)}
					{error}
				</LoginCard>
			</LoginContainer>
		</AuthLayout>
	);
};

const ResetPasswordForm = ({ isLoading, submit }) => {
	const { register, handleSubmit, errors } = useForm<ResetPasswordFormValues>();
	const intl = useIntl();
	return (
		<>
			<div style={{ marginBottom: 16, marginTop: 0 }}>
				<Typography variant="h5" style={{ fontWeight: 800, fontSize: "20px", margin: 0, marginBottom: 16 }}>
					{intl.formatMessage({ id: "RESET_PASSWORD.reset-form.title" })}
				</Typography>
				<Typography variant="caption" style={{ fontWeight: "normal", fontSize: "18px" }}>
					{intl.formatMessage({ id: "RESET_PASSWORD.reset-form.description" })}
				</Typography>
			</div>
			<form onSubmit={handleSubmit(submit)}>
				<div style={{ display: "flex", flexDirection: "column" }}>
					<InputGroup>
						<TextField
							inputRef={register({ required: true })}
							variant="outlined"
							fullWidth
							type="email"
							name="email"
							label={intl.formatMessage({ id: "RESET_PASSWORD.reset-form.field.email" })}
							autoComplete="off"
							disabled={isLoading}
							helperText={errors.email?.message}
							size="small"
							error={!!errors.email?.message}
							sx={{
								"& .MuiFormLabel-root": {
									fontSize: "18px",
								},
								"& .MuiInputBase-root": {
									fontWeight: 500,
									fontSize: "18px",
									borderRadius: "8px",
								},
							}}
						/>
					</InputGroup>
					<div style={{ margin: "8px 0px" }} />
					<InputGroup>
						<LoginButton
							type="submit"
							variant="contained"
							color="primary"
							fullWidth
							disableElevation
							disabled={isLoading}
						>
							{intl.formatMessage({ id: "RESET_PASSWORD.reset-form.btn.submit" })}
						</LoginButton>
					</InputGroup>
				</div>
			</form>
		</>
	);
};

export default ResetPasswordScreen;
